import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { useLoginMultiUser } from '@/lib/auth';
import {
  InvalidCredentialsError,
  MultiUserDisabledError,
  RateLimitedError,
} from '@/lib/api';

/**
 * Formulario de login multi-user (email + password).
 *
 * Diferencias con `LoginForm` (passphrase):
 * - Dos campos en lugar de uno.
 * - Mapea 401 a `InvalidCredentialsError` y muestra "Email o contraseña
 *   incorrectos" (no diferencia entre email inexistente y password mala —
 *   evita enumeration attacks).
 * - Enlaces a `/signup` y `/password-reset/request`.
 *
 * No reutilizamos `LoginForm` porque el estado interno y el contrato de la
 * mutation son distintos; un único componente con `if` por modo acoplaría
 * ambos flows y complicaría los tests.
 */
export function LoginFormMultiUser(): ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const loginMutation = useLoginMultiUser();

  const errorId = 'login-error';

  function getErrorMessage(): string | null {
    const err = loginMutation.error;
    if (!err) return null;
    if (err instanceof InvalidCredentialsError) {
      return 'Email o contraseña incorrectos.';
    }
    if (err instanceof MultiUserDisabledError) {
      return 'Esta instalación no permite login con email. Usa la passphrase.';
    }
    if (err instanceof RateLimitedError) {
      const seconds = err.retryAfterSeconds;
      if (seconds === null) {
        return 'Demasiados intentos. Inténtalo de nuevo en unos minutos.';
      }
      const minutes = Math.max(1, Math.ceil(seconds / 60));
      return `Demasiados intentos. Espera ${minutes} minuto${minutes === 1 ? '' : 's'}.`;
    }
    return 'No se pudo iniciar sesión. Comprueba que el backend está activo.';
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || !password || loginMutation.isPending) return;
    loginMutation.mutate({ email: email.trim(), password });
  }

  const errorMessage = getErrorMessage();

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Inicia sesión</h2>
        <p className="mt-1 text-sm text-slate-600">
          Introduce tu email y contraseña para acceder.
        </p>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-slate-700">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={errorMessage !== null}
          aria-describedby={errorMessage ? errorId : undefined}
          disabled={loginMutation.isPending}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-slate-700">
          Contraseña
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-invalid={errorMessage !== null}
          aria-describedby={errorMessage ? errorId : undefined}
          disabled={loginMutation.isPending}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      {errorMessage && (
        <p
          id={errorId}
          role="alert"
          className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={loginMutation.isPending || email.length === 0 || password.length === 0}
        aria-busy={loginMutation.isPending}
        className="w-full rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loginMutation.isPending ? 'Entrando…' : 'Iniciar sesión'}
      </button>

      <div className="flex flex-col gap-2 text-sm text-slate-600">
        <Link
          to="/password-reset/request"
          className="text-teal-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          ¿Olvidaste tu contraseña?
        </Link>
        <Link
          to="/signup"
          className="text-teal-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          ¿No tienes cuenta? Regístrate
        </Link>
      </div>
    </form>
  );
}
